import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../../context/ThemeContext';
import { fetchWithAuth } from '../../utils/api';
import {
  DollarSign,
  Percent,
  Save,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Settings,
  TestTube2,
  Radio,
} from 'lucide-react';
import { formatUSD } from '../../utils/formatAmount';
import './PayoutSettingsTab.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PayoutSettingsTab = () => {
  const { token } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  const [settings, setSettings] = useState({
    minimumWithdrawalAmount: '',
    payoutFeePercentage: '',
  });

  useEffect(() => {
    fetchPayoutSettings();
  }, [mode]);

  const fetchPayoutSettings = async () => {
    setIsLoading(true);
    try {
      const isTestnet = mode === 'test';
      const response = await fetchWithAuth(`${API_BASE_URL}/system-settings/payout?isTestnet=${isTestnet}`);

      const data = await response.json();

      if (data.success) {
        setSettings({
          minimumWithdrawalAmount: data.data.minimumWithdrawalAmount?.toString() || '10',
          payoutFeePercentage: data.data.payoutFeePercentage?.toString() || '1',
        });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to load settings' });
      }
    } catch (error) {
      console.error('Error fetching payout settings:', error);
      setMessage({ type: 'error', text: 'Failed to load payout settings' });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const minAmount = parseFloat(settings.minimumWithdrawalAmount);
    if (isNaN(minAmount) || minAmount < 0) {
      newErrors.minimumWithdrawalAmount = 'Minimum amount must be a positive number';
    }

    const feePercent = parseFloat(settings.payoutFeePercentage);
    if (isNaN(feePercent) || feePercent < 0 || feePercent > 100) {
      newErrors.payoutFeePercentage = 'Fee percentage must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const isTestnet = mode === 'test';
      const response = await fetchWithAuth(`${API_BASE_URL}/system-settings/payout`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          minimumWithdrawalAmount: parseFloat(settings.minimumWithdrawalAmount),
          payoutFeePercentage: parseFloat(settings.payoutFeePercentage),
          isTestnet,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Payout settings updated successfully' });
        setSettings({
          minimumWithdrawalAmount: data.data.minimumWithdrawalAmount?.toString() || '10',
          payoutFeePercentage: data.data.payoutFeePercentage?.toString() || '1',
        });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update settings' });
      }
    } catch (error) {
      console.error('Error saving payout settings:', error);
      setMessage({ type: 'error', text: 'Failed to save payout settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (isLoading) {
    return (
      <div className="payout-settings-loading">
        <RefreshCw size={24} className="spinning" />
        <span>Loading payout settings...</span>
      </div>
    );
  }

  return (
    <div className="payout-settings-tab rd-card rd-card-pad">
      <div className="payout-header">
        <div className="rd-icon-tile">
          <Settings size={20} />
        </div>
        <div className="payout-header-text">
          <h3>Payout Settings</h3>
          <p>Configure withdrawal limits and fee percentages for merchant payouts</p>
        </div>
        <span className={`rd-badge ${mode === 'test' ? 'accent' : 'ok'} payout-mode-indicator`}>
          {mode === 'test' ? <TestTube2 size={13} /> : <Radio size={13} />}
          {mode === 'test' ? 'Test Mode' : 'Live Mode'}
        </span>
      </div>

      {message.text && (
        <div className={`payout-message ${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
          <button onClick={() => setMessage({ type: '', text: '' })} className="payout-message-close">×</button>
        </div>
      )}

      <div className="payout-form">
        <div className="rd-field">
          <label className="rd-label payout-label">
            <DollarSign size={15} className="label-icon green" />
            Minimum Withdrawal Amount (USD)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={settings.minimumWithdrawalAmount}
            onChange={(e) => handleInputChange('minimumWithdrawalAmount', e.target.value)}
            placeholder="Enter minimum amount in dollars"
            className={`rd-input rd-mono ${errors.minimumWithdrawalAmount ? 'rd-input-error' : ''}`}
          />
          {errors.minimumWithdrawalAmount && (
            <span className="rd-error payout-error">
              <AlertCircle size={14} />
              {errors.minimumWithdrawalAmount}
            </span>
          )}
          <span className="rd-hint">
            Merchants must have at least this amount to request a payout
          </span>
        </div>

        <div className="rd-field">
          <label className="rd-label payout-label">
            <Percent size={15} className="label-icon orange" />
            Payout Fee Percentage
          </label>
          <div className="payout-input-suffix">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={settings.payoutFeePercentage}
              onChange={(e) => handleInputChange('payoutFeePercentage', e.target.value)}
              placeholder="1.00"
              className={`rd-input rd-mono ${errors.payoutFeePercentage ? 'rd-input-error' : ''}`}
            />
            <span className="suffix-mark">%</span>
          </div>
          {errors.payoutFeePercentage && (
            <span className="rd-error payout-error">
              <AlertCircle size={14} />
              {errors.payoutFeePercentage}
            </span>
          )}
          <span className="rd-hint">
            This percentage will be deducted from merchant payouts as a processing fee
          </span>
        </div>
      </div>

      <div className="example-calculation">
        <h4>Example Calculation</h4>
        <div className="calculation-content">
          <p>
            If a merchant requests a payout of <strong className="rd-mono">$100.00</strong>:
          </p>
          <p>
            Fee ({settings.payoutFeePercentage || '0'}%):{' '}
            <span className="fee-amount rd-mono">
              -${formatUSD((parseFloat(settings.payoutFeePercentage) || 0) * 100 / 100)}
            </span>
          </p>
          <p>
            Merchant receives:{' '}
            <span className="receive-amount rd-mono">
              ${formatUSD(100 - (parseFloat(settings.payoutFeePercentage) || 0))}
            </span>
          </p>
        </div>
      </div>

      <div className="payout-actions">
        <button
          className="rd-btn rd-btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <RefreshCw size={16} className="spinning" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save changes
            </>
          )}
        </button>
      </div>

      <style jsx>{`
        .payout-settings-tab.rd-card {
          padding: 22px 24px;
        }
        .payout-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding-bottom: 18px;
          border-bottom: 1px solid var(--line);
          margin-bottom: 22px;
        }
        .payout-header-text {
          flex: 1;
          min-width: 0;
        }
        .payout-header-text h3 {
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
          margin: 0 0 2px;
        }
        .payout-header-text p {
          font-size: 13px;
          color: var(--ink-secondary);
          margin: 0;
        }
        .payout-mode-indicator {
          flex-shrink: 0;
        }
        .payout-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: var(--r-md);
          margin-bottom: 22px;
          font-size: 14px;
          border: 1px solid var(--line);
        }
        .payout-message.success {
          background: var(--ok-soft);
          border-color: var(--ok-soft);
          color: var(--ok);
        }
        .payout-message.error {
          background: var(--err-soft);
          border-color: var(--err-soft);
          color: var(--err);
        }
        .payout-message span {
          flex: 1;
        }
        .payout-message-close {
          background: none;
          border: none;
          color: inherit;
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          opacity: 0.7;
        }
        .payout-message-close:hover {
          opacity: 1;
        }
        .payout-form {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 22px;
        }
        .payout-label {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .label-icon.green {
          color: var(--ok);
        }
        .label-icon.orange {
          color: var(--warn);
        }
        .payout-input-suffix {
          position: relative;
          display: flex;
          align-items: center;
        }
        .payout-input-suffix input {
          padding-right: 32px;
        }
        .suffix-mark {
          position: absolute;
          right: 14px;
          color: var(--ink-muted);
          font-size: 14px;
          pointer-events: none;
        }
        .payout-error {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .example-calculation {
          background: var(--bg-subtle);
          border: 1px solid var(--line);
          border-radius: var(--r-md);
          padding: 18px 20px;
          margin-bottom: 22px;
        }
        .example-calculation h4 {
          font-size: 13px;
          font-weight: 700;
          color: var(--ink);
          margin: 0 0 12px;
        }
        .calculation-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .calculation-content p {
          font-size: 13px;
          color: var(--ink-secondary);
          margin: 0;
        }
        .calculation-content strong {
          color: var(--ink);
        }
        .fee-amount {
          color: var(--warn);
          font-weight: 600;
        }
        .receive-amount {
          color: var(--ok);
          font-weight: 600;
        }
        .payout-actions {
          display: flex;
          justify-content: flex-end;
          padding-top: 20px;
          border-top: 1px solid var(--line);
        }
        .payout-actions .spinning {
          animation: spin 1s linear infinite;
        }
        @media (max-width: 768px) {
          .payout-form {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 576px) {
          .payout-settings-tab.rd-card {
            padding: 16px;
          }
          .payout-header {
            flex-wrap: wrap;
          }
          .payout-actions .rd-btn {
            width: 100%;
            min-height: 44px;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default PayoutSettingsTab;
